import { ExternalLinkedArtifact, ExternalLinkedArtifactIdentifier } from "TFS/WorkItemTracking/DataProviderContracts";
import { ILinkedArtifact } from "TFS/WorkItemTracking/ExtensionContracts";
import { resolveGitHubItems } from "WorkItemTracking/Scripts/OM/GitHubLinkDataSource";

export class GitHubLinkDataHelper {
    private readonly _resolvedGitHubLinksCache: { [artifactUri: string]: ILinkedArtifact } = {};

    public resolveGitHubLinks(workItemId: number, links: ILinkedArtifact[]): Promise<ILinkedArtifact[]> | ILinkedArtifact[] {
        const resolvedLinks: ILinkedArtifact[] = [];
        const linksToResolve: ILinkedArtifact[] = [];

        for (const link of links) {
            const cache = this._resolvedGitHubLinksCache[link.uri];
            if (cache) {
                resolvedLinks.push(cache);
            } else {
                linksToResolve.push(link);
            }
        }

        if (linksToResolve.length > 0) {
            return this._getGitHubLinksData(linksToResolve, workItemId).then(
                result => result.concat(resolvedLinks),
                () => [] // Return empty array if promise throws
            );
        } else {
            return resolvedLinks;
        }
    }

    private async _getGitHubLinksData(linksToResolve: ILinkedArtifact[], workItemId: number): Promise<ILinkedArtifact[]> {
        const linkIdentifers: ExternalLinkedArtifactIdentifier[] = linksToResolve.map(link => ({
            itemType: link.externalLinkedArtifactContext.itemType,
            repoInternalId: link.externalLinkedArtifactContext.repoInternalId,
            numberOrSHA: link.externalLinkedArtifactContext.numberOrSHA
        }));

        const response = await resolveGitHubItems(linkIdentifers, workItemId);

        const resolvedLinksData = response.reduce((map, linkData) => {
            const dataLookupKey = this._getGitHubLinkDataLookupKey(linkData);
            map[dataLookupKey] = linkData;
            return map;
        }, {});

        // Combine the external data with the artifact link
        const resolvedLinks: ILinkedArtifact[] = [];
        for (const link of linksToResolve) {
            const dataLookupKey = this._getGitHubLinkDataLookupKey(link.externalLinkedArtifactContext);
            const linkData = resolvedLinksData[dataLookupKey];
            const resolvedLink = { ...link, externalLinkedArtifactContext: linkData || link.externalLinkedArtifactContext };
            resolvedLinks.push(resolvedLink);

            // Cache the result
            this._resolvedGitHubLinksCache[resolvedLink.uri] = resolvedLink;
        }

        return resolvedLinks;
    }

    private _getGitHubLinkDataLookupKey(githubLinkData: ExternalLinkedArtifact): string {
        return `${githubLinkData.itemType}${githubLinkData.repoInternalId}${githubLinkData.numberOrSHA}`.toLowerCase();
    }
}
